import { Component } from '@angular/core';
import { User, UserAuthToken } from '@tytapp/models';
import { UserService } from '../user.service';
import { Logger, DialogComponent, NamedDialog, FormInput, FormComponent, AppConfig, sleep, PageService } from '@tytapp/common';
import { UsersApi } from '@tytapp/api';
import { environment } from '@tytapp/environment';
import { MessageDialogComponent } from '@tytapp/app/message-dialog/message-dialog.component';

@NamedDialog('signup')
@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignUpComponent extends DialogComponent implements FormComponent {

  constructor(
    private userService : UserService,
    private appConfig : AppConfig,
    private userApi : UsersApi,
    pageService : PageService
  ) { 
    super(pageService);
  }
  
  private disableRedirect : boolean = false;

  valuePropReason = "Stand with the only news network that stands **with you.**";
  reason = "Follow Your Favorites. Sign up for **free**";

  get recaptchaSiteKey() {
    return environment.recaptchaV2SiteKey;
  }

  captchaSolved(response : string) {
    console.log(`reCAPTCHA response: ${response}`);
    this.recaptchaToken = response;
  }

  @FormInput()
  recaptchaToken : string;

  @FormInput()
  username : string = "";

  @FormInput()
  password : string = "";

  @FormInput()
  rememberMe : boolean = false;

  @FormInput()
  email : string = "";

  @FormInput()
  firstName : string = "";

  @FormInput()
  lastName : string = "";

  @FormInput()
  zip : string = "";

  @FormInput()
  newsletter = true;

  oauthUrlFor(provider : string) {
    let queryString = this.buildQuery({
      app: 'web',
      source: this.userService.recentSource || 'free-signup',
      source_first_time: this.userService.sourceFirstTime ? 'true' : 'false',
      return_url: `/login/return?method=free`
    });

    return `${environment.urls.platform}/sign-in-with/${provider}?${queryString}`;
  }

  private buildQuery(params : Record<string,any>) {
    return Object.keys(params).map(key => `${key}=${encodeURIComponent(params[key])}`).join('&');
  }
  
  get joinUrl() {
    return environment.urls.purchase;
  }

  enableYouTubeLogin = false;
  
  async init(reasonOrParams : string | Object = null) {

    // Grab the last non-signup/login page the user visited 
    // and save it as the return URL

    let urls = this.shell.urlHistory.filter(x => !x.startsWith('/login') && !x.startsWith('/signup'));
    let url = urls[urls.length - 1];
    if (url)
      this.userService.bounceURL = url;

    this.enableReCAPTCHA = await this.appConfig.featureEnabled("apps.web.enable_recaptcha");

    let reason : string;

    if (reasonOrParams && typeof reasonOrParams === 'object') {
      let params : any = reasonOrParams;
      reason = params.reason;
    } else if (typeof reasonOrParams === 'string') {
      reason = <string>reasonOrParams;
    } else if (reasonOrParams) {
      throw new Error(`First parameter to SignupComponent.init() must be a string (the 'reason' message), an object (parameters from DialogGuard route), or a falsey value`);
    }

    if (reason)
      this.reason = reason;

    if (!this.router)
      throw "Failed to get a router";

    this.subscribe(this.userService.userChanged, user => {
      if (!user || this.disableRedirect)
        return;

      if (!this.justSignedUp)
        this.router.navigateByUrl('/'); 
    });

    this.enableYouTubeLogin = await this.appConfig.featureEnabled('apps.web.enable_youtube_login');
  }

  private justSignedUp = false;

  submitting = false;
  extendedEmailNote = false;
  enableReCAPTCHA = false;

  async submit() {

    // CRITICAL SECTION: NO ASYNC //
    if (this.submitting)
      return;
    this.submitting = true;
    this.extendedEmailNote = false;
    // CRITICAL SECTION: NO ASYNC //

    // Make sure the UI has updated before we proceed
    await sleep(0);
    
    let emailError = this.userService.validateEmail(this.email);
    if (emailError) {
      this.showError(emailError);
      this.submitting = false;
      this.extendedEmailNote = true;
      return;
    }
    
    this.disableRedirect = true;
    let userToken : UserAuthToken;

    try {
      userToken = <any> await this.userApi.signup({ 
        email: this.email,
        password: this.password,
        first_name: this.firstName,
        email_marketing_optin: this.newsletter,
        source: this.userService.recentSource || 'free-signup',
        // Not added in the backend yet, but may be useful in the future
        //source_stamp: (this.userService.sourceStamp || new Date()).toISOString(),
        source_first_time: this.userService.sourceFirstTime,
        zip: this.zip,
        recaptcha_token: this.recaptchaToken
      }).toPromise();
    } catch (e) {
        if (e.json)
          e = e.json();
        
        console.error("Received error while signing up:");
        console.dir(e);

        this.showError(e.message || e.error);
        setTimeout(() => this.submitting = false, 3000);

        return;
    }
    
    this.justSignedUp = true;

    if (!userToken.token) {
      this.shell.showDialog(MessageDialogComponent, "Confirm your email address", "You must confirm your email address before logging in. Please check your email to proceed.")
      this.submitting = false;
      return;
    }

    Logger.inspect("UserToken", userToken);

    this.userService.onboarding = true;
    
    // Save authentication first (before completeLogin) so that sendRegisteredBeacons 
    // knows what user just signed up
    
    this.userService.saveAuthentication(userToken.user, userToken.token);
    await this.userService.sendRegisteredBeacons();
    this.userService.completeLogin(userToken.user, userToken.token);
  }
}
